'use client';

/**
 * Renders a base64 `MediaBlob` ({_b64,_mime,_bytes,_w,_h,_truncated,_label})
 * as a real <img>/<video>/<audio> element (image → click-to-lightbox). If the
 * blob is truncated, shows the thumbnail + a "load full" button that fetches
 * /api/debug/agent-event/{eventId}?callId=... and swaps in the full blob.
 * See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.5 / §6.4.
 */
import React, { useState, useCallback } from 'react';
import { Maximize2, Download } from 'lucide-react';
import { useModalA11y } from '@/lib/a11y/useModalA11y';
import { findMediaBlobs, type MediaBlob as MediaBlobT } from '@/lib/debug/types';

function dataUri(b: MediaBlobT): string {
  return `data:${b._mime};base64,${b._b64}`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function MediaBlob({
  blob,
  eventId,
  callId,
}: {
  blob: MediaBlobT;
  eventId?: string;
  callId?: string | null;
}) {
  const [full, setFull] = useState<MediaBlobT | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const b = full ?? blob;
  const uri = dataUri(b);
  const isImage = b._mime.startsWith('image/');
  const isVideo = b._mime.startsWith('video/');
  const isAudio = b._mime.startsWith('audio/');

  const loadFull = useCallback(async () => {
    if (!eventId || !callId || loadingFull) return;
    setLoadingFull(true);
    try {
      const res = await fetch(`/api/debug/agent-event/${encodeURIComponent(eventId)}?callId=${encodeURIComponent(callId)}`);
      if (res.ok) {
        const ev = await res.json();
        const blobs = findMediaBlobs(ev?.payload);
        // Match by _label if present, else by mime, else first.
        const match =
          blobs.find((x) => x._label && x._label === blob._label) ||
          blobs.find((x) => x._mime === blob._mime) ||
          blobs[0];
        if (match && !match._truncated) setFull(match);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingFull(false);
    }
  }, [eventId, callId, loadingFull, blob._label, blob._mime]);

  const downloadBlob = useCallback(() => {
    const a = document.createElement('a');
    a.href = uri;
    a.download = b._label?.replace(/[^\w.-]/g, '_') || `blob.${(b._mime.split('/')[1] || 'bin')}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [uri, b._label, b._mime]);

  const closeLightbox = useCallback(() => setLightbox(false), []);
  const { ref: lightboxRef, props: lightboxProps } = useModalA11y({ onClose: closeLightbox, open: lightbox && isImage });

  const caption = (
    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
      {b._label && <span className="truncate">{b._label}</span>}
      <span>{b._mime}</span>
      <span>{fmtBytes(b._bytes)}{b._truncated && !full ? ' (thumbnail)' : ''}</span>
      {b._w && b._h ? <span>{b._w}×{b._h}</span> : null}
      {b._truncated && !full && eventId && callId && (
        <button
          onClick={loadFull}
          className="text-blue-400 hover:text-blue-300 disabled:opacity-50"
          disabled={loadingFull}
        >
          {loadingFull ? 'loading…' : 'load full'}
        </button>
      )}
      <button onClick={downloadBlob} className="text-slate-400 hover:text-slate-200" title="Download">
        <Download className="inline h-3 w-3" />
      </button>
      {isImage && (
        <button onClick={() => setLightbox(true)} className="text-slate-400 hover:text-slate-200" title="Expand">
          <Maximize2 className="inline h-3 w-3" />
        </button>
      )}
    </div>
  );

  return (
    <div className="my-1 inline-block max-w-full align-top">
      {isImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={uri}
          alt={b._label || 'debug image'}
          onClick={() => setLightbox(true)}
          className="max-h-48 max-w-full cursor-zoom-in rounded border border-white/[0.12] bg-black/40 object-contain"
        />
      )}
      {isVideo && <video src={uri} controls className="max-h-64 max-w-full rounded border border-white/[0.12] bg-black" />}
      {isAudio && <audio src={uri} controls className="w-full" />}
      {!isImage && !isVideo && !isAudio && (
        <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-0.5 font-mono text-[11px] text-slate-300">
          binary · {b._mime} · {fmtBytes(b._bytes)}
        </span>
      )}
      {caption}

      {lightbox && isImage && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-6" role="presentation">
          <div className="absolute inset-0 bg-black/80" role="presentation" onClick={closeLightbox} />
          <div ref={lightboxRef} {...lightboxProps} aria-label="Image preview" className="relative flex max-h-full max-w-full items-center justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={uri} alt={b._label || 'debug image'} className="max-h-full max-w-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaBlob;
